import React, { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Turnstile from "@/components/Turnstile";
import { toast } from "sonner";

const Register = () => {
  const { register } = useApp();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const onTsToken = useCallback((t) => setTsToken(t), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, tsToken);
      toast.success("Account created", { description: "Check your inbox to confirm your email." });
      navigate("/");
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many attempts", { description: err.response?.data?.detail || "Please wait a minute and try again." });
      } else {
        toast.error("Registration failed", { description: err.response?.data?.detail || "Try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-24">
        <div className="overline text-accent">Create account</div>
        <h1 className="font-display text-5xl mt-3 mb-10 leading-none tracking-tighter">Start collecting.</h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="name" className="overline text-muted-foreground">Full name</Label>
            <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-none mt-2 h-12" />
          </div>
          <div>
            <Label htmlFor="email" className="overline text-muted-foreground">Email</Label>
            <Input id="email" data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none mt-2 h-12" />
          </div>
          <div>
            <Label htmlFor="password" className="overline text-muted-foreground">Password</Label>
            <Input id="password" data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="rounded-none mt-2 h-12" />
          </div>
          <Turnstile onToken={onTsToken} action="register" />
          <Button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full rounded-none h-12 bg-primary">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>

        <div className="my-8 relative flex items-center justify-center">
          <div className="absolute inset-x-0 border-t border-border" />
          <span className="relative bg-background px-4 overline text-muted-foreground">Or</span>
        </div>

        <Button onClick={handleGoogle} data-testid="google-register-btn" variant="outline" className="w-full rounded-none h-12 border-foreground">
          Continue with Google
        </Button>

        <p className="mt-8 text-sm text-muted-foreground text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-accent hover:underline" data-testid="link-login">Sign in</Link>
        </p>
      </div>
    </Layout>
  );
};

export default Register;
